import { useCallback, useMemo, useState } from 'react'

import { type SelectOption } from '../../../../components/SelectDropdown/SelectDropdown'
import { type User } from '../../../../models/User'
import { useAppSelector } from '../../../../store/hooks'
import { selectAccountId } from '../../../../store/auth/authSlice'
import { useGetIntegrationUsersQuery } from '../../../../store/integrations/integrations'
import { getContactOptions } from '../utils'
import { type IntegrationServiceId } from '../../../../common/integrations'

export interface ContactProviderResult {
  contactOptions: SelectOption[]
  contacts: User[]
  isLoading: boolean
  isError: boolean
  contactsError: unknown
  onSearchInputChange: (input: string) => Promise<void>
  findContactByOptionId: (optionId: string) => User | undefined
}

export const useCRMContactProvider = (serviceId: IntegrationServiceId, skip?: boolean): ContactProviderResult => {
  const accountId = useAppSelector(selectAccountId)
  const [searchTerm, setSearchTerm] = useState<string | null>(null)

  const {
    data: contactsData,
    isError,
    error: contactsError,
    isFetching: isLoading,
  } = useGetIntegrationUsersQuery(
    {
      accountId,
      connector: serviceId,
      filters: searchTerm ? { name: searchTerm } : undefined,
    },
    { skip: skip || !accountId || !serviceId },
  )

  const contacts = useMemo(() => contactsData?.data || [], [contactsData])
  const contactOptions = useMemo(() => getContactOptions(contacts, serviceId), [contacts, serviceId])

  const onSearchInputChange = useCallback(async (input: string) => {
    setSearchTerm(input)
  }, [])

  const findContactByOptionId = useCallback(
    (optionId: string) => contacts.find((contact) => contact.integrations?.find((i) => i.serviceId === serviceId)?.externalId === optionId),
    [contacts, serviceId],
  )

  return { contactOptions, contacts, isLoading, isError, contactsError, onSearchInputChange, findContactByOptionId }
}
